import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output
} from '@angular/core';
import { FormArray, FormGroup, Validators } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { createMask } from '@ngneat/input-mask';
import * as _ from 'lodash';
import { CardColumnSelectionBase } from 'src/app/classes/base-classes/card-column/card-column-selection-base';
import { CanadianWorkersCompensationForm } from 'src/app/classes/form-groups/canadian-workers-compensation-form';
import { CompanyVendorForm } from 'src/app/classes/form-groups/company-vendor-form';
import { InsuranceAgentForm } from 'src/app/classes/form-groups/insurance-agent-form';
import { Address, Company } from 'src/app/classes/models';
import { InsuranceAgent } from 'src/app/classes/models/insurance-agent';
import { Card } from 'src/app/interfaces/card';
import { InsuranceAgentProviderPolicies } from 'src/app/interfaces/insurance-agent-provider-policies';
import { InsuranceAgentProviderType } from 'src/app/interfaces/insurance-agent-provider-type';
import { LocalizationInfo } from 'src/app/interfaces/localization-info';
import { ObjectFieldSelectOption } from 'src/app/interfaces/object-field-select-option';
import { State } from 'src/app/interfaces/state';
import { CardColumnDisplayModalComponent } from 'src/app/modals/card-column-modal/card-column-display-modal/card-column-display-modal.component';
import { ConfirmModalComponent } from 'src/app/modals/confirm-modal/confirm-modal.component';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';

@Component({
  selector: 'app-insurance-agent-edit',
  templateUrl: './insurance-agent-edit.component.html',
  styleUrls: ['./insurance-agent-edit.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class InsuranceAgentEditComponent implements OnInit {
  constructor(
    private ToastService: ToastService,
    private UtilityService: UtilityService,
    private ModalService: NgbModal
  ) {}

  @Input() VendorId!: number;
  @Input() country!: 'US' | 'CA';
  @Input() InsuranceAgentFormGroup = new FormGroup(new InsuranceAgentForm());
  @Input() CompanyVendorFormGroup!: CompanyVendorForm;
  // This is used for Canadian Vendors to display the appropriate WSIB/WCB information.
  @Input() VendorMailingAddress!: Address;
  // Hide when VIVE Admin is adding/editing agent. Use modal save button.
  @Input() hideSaveButton: boolean = false;

  @Input() currentInsuranceAgentPolicies!: (
    | {
        label: 'General Liability';
        value: 'isGLProvider';
      }
    | {
        label: 'Commercial Auto Liability';
        value: 'isAutoProvider';
      }
    | {
        label: 'Umbrella Liability';
        value: 'isUmbrellaProvider';
      }
    | {
        label: "Worker's Compensation";
        value: 'isWorkersProvider';
      }
    | {
        label: 'Professional Liability';
        value: 'isProfessionalProvider';
      }
  )[];

  // Shared states between Client & Vendor.
  @Input() SharedOperationStates = new Set<State>();

  // If available, pass insurance agents in to limit agent type selection
  // to types not assigned to existing agents.
  @Input() InsuranceAgents: InsuranceAgent[] = [];

  AvailablePolicyTypes = new Set<InsuranceAgentProviderType>();

  @Output() saveInsuranceAgent = new EventEmitter<boolean>();

  Company!: Company;
  LocalizationInfo!: LocalizationInfo;

  agentProviderFieldMap: InsuranceAgentProviderPolicies = {
    isGLProvider: 'General Liability',
    isAutoProvider: 'Commercial Auto Liability',
    isUmbrellaProvider: 'Umbrella Liability',
    isWorkersProvider: "Worker's Compensation",
    isProfessionalProvider: 'Professional Liability'
  };

  phoneMask = createMask('(999) 999-9999');
  numberMask = createMask({ regex: '[0-9]*' });
  alphanumericMask = createMask({ regex: '[a-zA-Z0-9]*' });
  emailRegex = this.UtilityService.getRegex('email');
  phoneExtensionMask = createMask({ regex: '\\d{1,6}', placeholder: '' });

  updateAvailablePolicyTypes() {
    this.AvailablePolicyTypes.clear();
    const InsuranceAgentPolicyTypes: InsuranceAgentProviderType[] = [
      'isGLProvider',
      'isAutoProvider',
      'isUmbrellaProvider',
      'isWorkersProvider',
      'isProfessionalProvider'
    ];

    const UnavailablePolicyTypes: {
      [key in InsuranceAgentProviderType]?: boolean;
    } = Object.assign(
      {},
      ...this.InsuranceAgents.map((InsuranceAgent) => {
        const AssignedPolicyTypes: {
          [key in InsuranceAgentProviderType]?: boolean;
        } = {};
        InsuranceAgentPolicyTypes.forEach((policyType) => {
          if (InsuranceAgent[policyType]) {
            AssignedPolicyTypes[policyType] = true;
          }
        });
        return AssignedPolicyTypes;
      })
    );

    InsuranceAgentPolicyTypes.forEach((policyType) => {
      if (!UnavailablePolicyTypes[policyType]) {
        this.AvailablePolicyTypes.add(policyType);
      }
    });
  }

  saveAgent() {
    this.saveInsuranceAgent.emit(true);
  }

  processFormChanges(data: null | InsuranceAgent, reset: boolean = false) {
    if (reset) {
      this.InsuranceAgentFormGroup.reset();
      this.InsuranceAgentFormGroup.patchValue({ VendorId: this.VendorId });
    }
    if (data) {
      this.InsuranceAgentFormGroup.patchValue(data);
    }
    this.populateCurrentInsuranceAgentPoliciesArray();
  }

  resetForm() {
    const confirmModalInstance = this.ModalService.open(ConfirmModalComponent, {
      backdrop: 'static',
      modalDialogClass: 'confirm-modal'
    });
    _.assign(confirmModalInstance.componentInstance, {
      displayText: 'Are you sure you want to reset this form?'
    });

    confirmModalInstance.result.then((confirm) => {
      if (confirm) {
        this.processFormChanges(null, true);
      }
    });
  }

  // This function mutates an existing array (currentInsuranceAgentPolicies). It populates
  // this array with the current insurance agent's policies. Currently this is called when
  // the current insurance agent has their policies changed.
  populateCurrentInsuranceAgentPoliciesArray() {
    this.currentInsuranceAgentPolicies.splice(
      0,
      this.currentInsuranceAgentPolicies.length
    );
    const currentInsuranceAgent = this.InsuranceAgentFormGroup.value;
    _.each(this.agentProviderFieldMap, (value, key) => {
      if (currentInsuranceAgent[key as InsuranceAgentProviderType]) {
        this.currentInsuranceAgentPolicies.push({
          label: value,
          value: key as
            | 'isGLProvider'
            | 'isAutoProvider'
            | 'isUmbrellaProvider'
            | 'isWorkersProvider'
            | 'isProfessionalProvider'
        } as
          | {
              label: 'General Liability';
              value: 'isGLProvider';
            }
          | {
              label: 'Commercial Auto Liability';
              value: 'isAutoProvider';
            }
          | {
              label: 'Umbrella Liability';
              value: 'isUmbrellaProvider';
            }
          | {
              label: "Worker's Compensation";
              value: 'isWorkersProvider';
            }
          | {
              label: 'Professional Liability';
              value: 'isProfessionalProvider';
            });
      }
    });
  }

  deselectPolicy(
    policyIndex: number,
    agentProviderField:
      | 'isGLProvider'
      | 'isAutoProvider'
      | 'isUmbrellaProvider'
      | 'isWorkersProvider'
      | 'isProfessionalProvider'
  ) {
    this.currentInsuranceAgentPolicies.splice(policyIndex, 1);
    this.InsuranceAgentFormGroup.patchValue({ [agentProviderField]: false });
    this.InsuranceAgentFormGroup.markAsDirty();
  }

  choosePolicyTypes() {
    this.updateAvailablePolicyTypes();
    const insuranceAgentFormValues = this.InsuranceAgentFormGroup.value;
    const policiesModal: NgbModalRef = this.ModalService.open(
      CardColumnDisplayModalComponent,
      { backdrop: 'static', modalDialogClass: 'card-column-display-modal' }
    );

    const PolicyTypeToCard: { [key in InsuranceAgentProviderType]: Card } = {
      isGLProvider: {
        bodyText: 'General Liability',
        record: {
          recordField: 'isGLProvider',
          selectValue: true,
          deselectValue: false
        } as ObjectFieldSelectOption
      },
      isAutoProvider: {
        bodyText: 'Commercial Auto Liability',
        record: {
          recordField: 'isAutoProvider',
          selectValue: true,
          deselectValue: false
        } as ObjectFieldSelectOption
      },
      isUmbrellaProvider: {
        bodyText: 'Umbrella Liability',
        record: {
          recordField: 'isUmbrellaProvider',
          selectValue: true,
          deselectValue: false
        } as ObjectFieldSelectOption
      },
      isWorkersProvider: {
        bodyText: "Workers' Compensation",
        record: {
          recordField: 'isWorkersProvider',
          selectValue: true,
          deselectValue: false
        } as ObjectFieldSelectOption
      },
      isProfessionalProvider: {
        bodyText: 'Professional Liability',
        record: {
          recordField: 'isProfessionalProvider',
          selectValue: true,
          deselectValue: false
        } as ObjectFieldSelectOption
      }
    };

    const cardCollection: Card[] = [];

    this.AvailablePolicyTypes.forEach((policyType) => {
      if (
        policyType !== 'isWorkersProvider' ||
        (policyType === 'isWorkersProvider' && this.country === 'US')
      ) {
        cardCollection.push(PolicyTypeToCard[policyType]);
      }
    });

    const selectionModel = new CardColumnSelectionBase(
      'multiple',
      insuranceAgentFormValues
    );

    _.assign(policiesModal.componentInstance, {
      title: 'Select Policy Type(s)',
      cardCollection,
      selectionModel
    });

    policiesModal.result.then((dataObj: any) => {
      // If every value returned from dataObj matches the value in the associated insurance agent form's form control,
      // nothing has changed.
      if (
        dataObj &&
        !_.every(
          dataObj,
          (value, key) =>
            value ===
            insuranceAgentFormValues[
              key as keyof typeof insuranceAgentFormValues
            ]
        )
      ) {
        this.InsuranceAgentFormGroup.markAsDirty();
        this.processFormChanges(dataObj);
      }
    });
  }

  ngOnInit(): void {
    if (!this.VendorId) {
      throw this.ToastService.addErrorToast('Error', 'Missing Vendor Id.', {
        returnError: true
      });
    }

    if (!this.country) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing country information.',
        { returnError: true }
      );
    }

    if (!this.VendorMailingAddress) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Vendor Mailing Address.',
        { returnError: true }
      );
    }

    if (!this.currentInsuranceAgentPolicies) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Insurance Policies handled by agent.',
        { returnError: true }
      );
    }

    if (!this.SharedOperationStates) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing shared operation states.',
        { returnError: true }
      );
    }

    const {
      controls: {
        id: { value: CompanyVendorId },
        CompanyId: { value: CompanyId },
        VendorId: { value: VendorId },
        isWorkersCompExempt: { value: isWorkersCompExempt },
        CAWCExempt: { value: CAWCExempt },
        Company: CompanyFormGroup
      }
    } = this.CompanyVendorFormGroup;

    if (!CompanyVendorId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Client/Vendor form is missing id.',
        { returnError: true }
      );
    }

    if (!CompanyId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Client/Vendor form is missing Client id.',
        { returnError: true }
      );
    }

    if (!VendorId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Client/Vendor form is missing Vendor id.',
        { returnError: true }
      );
    }

    if (!CompanyFormGroup) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Client/Vendor form is missing Client information.',
        { returnError: true }
      );
    }

    const { value: Company } = CompanyFormGroup;

    if (!Company) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Client information.',
        { returnError: true }
      );
    }

    if (this.country === 'CA') {
      if (
        !this.CompanyVendorFormGroup.controls['CanadianWorkersCompensations']
      ) {
        // Add WSIB/WCB Based on operation states
        this.CompanyVendorFormGroup.setControl(
          'CanadianWorkersCompensations',
          new FormArray([])
        );
      }

      const CanadianWorkersCompensationsFormArray =
        this.CompanyVendorFormGroup.controls['CanadianWorkersCompensations'];

      const CWCFormGroups: {
        WSIB: CanadianWorkersCompensationForm | null;
        WCB: CanadianWorkersCompensationForm | null;
      } = {
        WSIB: null,
        WCB: null
      };

      CanadianWorkersCompensationsFormArray!.controls.forEach(
        (cwcFormGroup) => {
          CWCFormGroups[cwcFormGroup.controls['type'].value!] = cwcFormGroup;
        }
      );

      this.SharedOperationStates.forEach((state) => {
        if (state === 'ON' && !CWCFormGroups['WSIB']) {
          // Create form group.
          const CanadianWorkersCompensationFormGroup =
            new CanadianWorkersCompensationForm();
          CanadianWorkersCompensationFormGroup.patchValue({
            CompanyId: CompanyId!,
            VendorId: VendorId!,
            CompanyVendorId,
            type: 'WSIB'
          });
          CanadianWorkersCompensationsFormArray!.push(
            CanadianWorkersCompensationFormGroup
          );
        }

        if (state === 'AB' && !CWCFormGroups['WCB']) {
          const CanadianWorkersCompensationFormGroup =
            new CanadianWorkersCompensationForm();
          CanadianWorkersCompensationFormGroup.patchValue({
            CompanyId: CompanyId!,
            VendorId: VendorId!,
            CompanyVendorId,
            type: 'WCB'
          });
          CanadianWorkersCompensationsFormArray!.push(
            CanadianWorkersCompensationFormGroup
          );
        }
      });

      CanadianWorkersCompensationsFormArray?.controls.forEach(
        (canadianCompensationForm) => {
          // These form fields are not required during registration and get handled by admins later.
          const nonRequiredControls = ['CAWCDate', 'CAWCStatus'];

          if (isWorkersCompExempt || CAWCExempt) {
            nonRequiredControls.push('CAWCNumber');
          }

          nonRequiredControls.forEach((controlName) => {
            const control = canadianCompensationForm.get(controlName);

            if (control) {
              control.removeValidators([Validators.required]);
              control.updateValueAndValidity();
            }
          });
        }
      );
    }

    Object.assign(this, { Company });

    this.LocalizationInfo = this.UtilityService.getLocalizedValuesMap(
      this.country
    );

    this.populateCurrentInsuranceAgentPoliciesArray();
  }
}
